import React from 'react';
import {
  castArray,
  difference,
  find,
  keyBy,
  compact,
  isEmpty,
} from 'lodash';
import I18n from 'i18n-js';
import classNames from 'classnames';

import {
  core_apimessages_ProductSpec_Type,
  reverb_search_Filter_WidgetType as WidgetType,
} from '../gql/graphql';
import SelectFacet from './select_facet';
import { Filter } from './search_filters';
import { CSP2FiltersOption } from './marketplace_filter_row';
import { RCPopover, RCChip } from '@reverbdotcom/cadence/components';
import { Location } from 'history';

const APPROVED_FRETBOARD_FILTER_CATEGORIES = [
  'dfd39027-d134-4353-b9e4-57dc6be791b9', // electric guitars
  '3ca3eb03-7eac-477d-b253-15ce603d2550', // acoustic guitars
  '53a9c7d7-d73d-4e7f-905c-553503e50a90', // bass guitars
];

interface ICSPFiltersOption {
  name: string;
  total: number;
  selected: boolean;
  optionValue?: string;
  setValues?: any;
  unsetValues?: any;
  paramName?: string;
}

// Finish and fretboard material filters get special treatment on CSPs - they
// appear in dropdowns above the grid in addition to the sidebar, and they
// only allow selecting one value at a time - so we manually create them here
// and supply them to the marketplace filter row
function getFinishFilters(filtersAndListingsData, csp, location): ICSPFiltersOption[] {
  const {
    canonicalFinish = '',
  } = location?.query ?? {};
  const finishAgg = filtersAndListingsData?.finishAgg?.aggregationResults[0]?.aggregationDetails || [];
  const totalByFinish = keyBy(finishAgg, 'key');
  const total = finish => (totalByFinish[finish]?.count || 0);

  const finishProductSpecs = (csp?.specs?.productSpecs || []).filter(
    spec => spec.type === core_apimessages_ProductSpec_Type.FINISH,
  );
  const isFinishFilterSelected = finish => (canonicalFinish === finish);

  const options = finishProductSpecs
    .map((spec) => {
      return {
        name: spec.value,
        selected: isFinishFilterSelected(spec.value),
        total: total(spec.value),
        optionValue: spec.value,
        setValues: isFinishFilterSelected(spec.value) ? [] : [spec.value],
        unsetValues: isFinishFilterSelected(spec.value) ? [spec.value] : [],
        paramName: 'canonicalFinish',
      };
    });

  return options;
}

function getFretboardMaterialFilters(csp, filtersAndListingsData, location): CSP2FiltersOption[] {
  if (!APPROVED_FRETBOARD_FILTER_CATEGORIES.includes(csp?.categories[0]?.parentCategories[0]?.id)) return [];

  const fretboardMaterialAgg = filtersAndListingsData?.fretboardMaterialAgg?.aggregationResults[0]?.aggregationDetails || [];
  if (fretboardMaterialAgg.length <= 1) return [];

  const traitValues = location?.query?.trait_values ?? [];
  const totalByMaterial = keyBy(fretboardMaterialAgg, 'displayName');
  const total = material => (totalByMaterial[material]?.count || 0);
  const isFretboardMaterialSelected = material => (traitValues.includes(material));

  const selectedFretboardMaterial = find(fretboardMaterialAgg.map(option => option.key), isFretboardMaterialSelected);
  const nonFretboardTraits = difference(castArray(traitValues), [selectedFretboardMaterial]);
  const paramName = 'trait_values';

  // This custom filter shares the trait_values param name with other default
  // trait filters. To accommodate this and make sure other trait filters work
  // as expected, we have some custom logic for setValues and unsetValues
  // below to make sure any other non-fretboard selected traits are preserved.
  const options = fretboardMaterialAgg
    .map(spec => {
      return {
        name: spec.displayName,
        selected: isFretboardMaterialSelected(spec.key),
        total: total(spec.displayName),
        optionValue: spec.key,
        setValues: isFretboardMaterialSelected(spec.key) ? nonFretboardTraits : nonFretboardTraits.concat(spec.key),
        unsetValues: isFretboardMaterialSelected(spec.key) ? nonFretboardTraits.concat(spec.key) : nonFretboardTraits,
        paramName,
      };
    });

  const allFretboardMaterialsOption = {
    name: I18n.t('commons.marketplaceFilters.allFretboardMaterials'),
    selected: !selectedFretboardMaterial,
    total: undefined,
    optionValue: '',
    setValues: nonFretboardTraits,
    unsetValues: nonFretboardTraits,
    paramName,
    all: true,
  };

  return [
    allFretboardMaterialsOption,
    ...options.sort((a, b) => {
      return b.total - a.total;
    }).map(option => {
      return {
        ...option,
        count: {
          value: option.total,
        },
      };
    }),
  ];
}


function buildFinishFilter(finishOptions: CSP2FiltersOption[]): Filter {
  const finishFilter = {
    name: 'Finish',
    widgetType: WidgetType.RADIO,
    aggregationName: 'finish',
    key: 'finishOptions',
    options: [],
  };

  if (!finishOptions || finishOptions.length <= 1) return finishFilter;

  const selected = finishOptions.find(opt => opt.selected);

  const allFinishesOption = {
    name: I18n.t('commons.marketplaceFilters.allFinishes'),
    selected: !selected,
    optionValue: '',
    setValues: [],
    unsetValues: [],
    paramName: 'canonicalFinish',
    all: true,
  };

  return {
    ...finishFilter,
    options: [
      allFinishesOption,
      ...finishOptions.sort((a, b) => {
        return b.total - a.total;
      }).map(option => {
        return {
          ...option,
          count: {
            value: option.total,
          },
        };
      }),
    ],
  };
}

function buildFretboardMaterialFilter(fretboardMaterialOptions: CSP2FiltersOption[]): Filter {
  const fretboardMaterialFilter = {
    name: 'Fretboard Material',
    widgetType: WidgetType.RADIO,
    aggregationName: 'fretboardMaterial',
    key: 'fretboardMaterialOptions',
    options: [],
  };

  if (!fretboardMaterialOptions || fretboardMaterialOptions.length <= 1) return fretboardMaterialFilter;

  return {
    ...fretboardMaterialFilter,
    options: fretboardMaterialOptions,
  };
}

export interface TraitQueryVariables {
  traitValues: string[];
  canonicalFinishes: string[];
  canonicalFinish: string;
}

export function traitQueryParams(location: Location): TraitQueryVariables {
  const queryParams = location?.query || {};
  const canonicalFinish = queryParams.canonicalFinish;

  return {
    traitValues: compact([(queryParams.trait_values)]).flat(),
    canonicalFinish: canonicalFinish || '',
    canonicalFinishes: compact([canonicalFinish]),
  };
}

export function finishAndFretboardTraitFilterOptions(csp, filtersAndListingsData, location): Filter[] {
  const finishFilters = buildFinishFilter(getFinishFilters(filtersAndListingsData, csp, location));
  const fretboardFilters = buildFretboardMaterialFilter(getFretboardMaterialFilters(csp, filtersAndListingsData, location));

  return [finishFilters, fretboardFilters];
}

export function emptyTraitFilters(filters: Filter[]) {
  return filters.every(f => isEmpty(f.options));
}

interface IProps {
  loading: boolean;
  filter: Filter;
  dynamicTitle: boolean;
  showOnMobile: boolean;
  displayOnlyIfSelected?: boolean;
}

export function TraitFilter({
  loading,
  filter,
  dynamicTitle,
  showOnMobile,
  displayOnlyIfSelected = false,
}: IProps) {
  const [popoverIsOpen, openPopover] = React.useState(false);
  if (!filter) return null;
  if (filter.options?.length <= 1) return null;

  const selected = filter.options.find(opt => opt.selected && !opt.all);
  if (displayOnlyIfSelected && !selected) return null;

  const classes = classNames(
    'marketplace-filter',
    { 'marketplace-filter--mobile-hidden': !showOnMobile },
  );

  return (
    <div className={classes}>
      <RCPopover
        isOpen={popoverIsOpen}
        onDismiss={() => openPopover(false)}
        position="bottom-start"
        loading={loading}
        anchor={
          <RCChip
            loading={loading}
            selected={!!selected}
            onClick={() => openPopover(!popoverIsOpen)}
          >
            {(dynamicTitle && selected) && selected.name}
            {(!dynamicTitle || !selected) && filter.name}
          </RCChip>
        }
      >
        <SelectFacet
          pageSection="filter-row"
          filter={filter}
          trackingQuery={''}
        />
      </RCPopover>
    </div>
  );
}
